declare global {
  interface Window {
    stageLoaded: boolean;
  }
}
declare global {
  interface Window {
    stage: any;
  }
}
declare global {
  interface Window {
    setCameraView: any;
  }
}
declare global {
  interface Window {
    captureScreenshot: any;
  }
}
import {
  ViewerApp,
  AssetManagerPlugin,
  GroundPlugin,
  CameraViewPlugin,
  CanvasSnipperPlugin,
  AssetExporterPlugin,
  downloadBlob,
  TemporalAAPlugin,
  ContactShadowGroundPlugin,
  AssetImporter,
  CameraView,
  ImportAddOptions,
  GLTFDracoExportPlugin,
} from "webgi";
import "./styles.css";
import { addBasePlugins } from "./utils/addBasePlugin";
import getAngleData, { CameraAngleType } from "./utils/getAngleData";
import {
  changeHandColor,
  changeMetalColor,
  getBoundView,
  initCameraParams,
  initModalParams,
  initModelMaterials,
} from "./utils/initScene";
import { initInfo, showLoaderUrl, showLogoLoader } from "./utils/loaderControl";
import loadHandModel from "./utils/loadHand";

class ViewerSetup {
  private viewer!: ViewerApp;
  private sceneMetalColor: string = "whiteGold";
  private camId: string = "2";
  private _handColor: string = "skin_2";
  private _showHand: boolean = false;
  private _angle: string = "";
  private _handSize: string = "";
  private originalView!: CameraView;
  productName!: string;

  constructor() {
    window.stage = this;
    window.captureScreenshot = this.captureScreenshot;
    window.setCameraView = this.changeCameraView;
    this.viewer = new ViewerApp({
      canvas: document.getElementById("ole-canvas") as HTMLCanvasElement,
      useRgbm: true,
    })!;
    this.viewer.renderer.addEventListener("contextLost", () => {
      showLogoLoader(true);
    });
    this.viewer.renderer.addEventListener("contextRestored", () => {
      showLogoLoader(false);
    });
    this.viewer.renderer.displayCanvasScaling = window.devicePixelRatio > 2 ? 2 : window.devicePixelRatio;

    this.setupViewer();
    // --- Listen to post message ---
    window.addEventListener("message", (e) => {
      const promiseData = this.onMessage(e);
      Promise.resolve(promiseData);
    });
  }

  private async setupViewer() {

    // --- Get query params (if any) ---
    const searchParams = window.location.search;
    const searchParmObject = new URLSearchParams(searchParams);
    let canvasMode = searchParmObject.get("mode") || "";
    let downloadFile = searchParmObject.get("downloadFile") || "";
    let preDownload = searchParmObject.get("preDownload") || "";
    let loaderUrl = searchParmObject.get("loaderUrl");
    let fileUrl = searchParmObject.get("fileUrl");
    this.sceneMetalColor = searchParmObject.get("metalColor") || "whiteGold";
    let camId: string = searchParmObject.get("camId") || "2";
    let searchCamAngle: string =
      searchParmObject.get("camAngle") || "defaultView";
    let camAngle: CameraAngleType = searchCamAngle as CameraAngleType;
    this._handColor = "skin_2";
    this._angle = '';
    this._showHand = Boolean(searchParmObject.get("showHand")) || false;
    this.productName = searchParmObject.get("productName") || "scene";


    this.camId = camId;

    // --- Set preloader src based on the query params ---
    if (loaderUrl) {
      showLoaderUrl(loaderUrl);
    }

    // --- Add required plugins ---
    await addBasePlugins(this.viewer, {
      canvasMode,
      downloadFile,
      camId,
      preDownload,
    });

    // --- Get required plugins ---
    const manager = this.viewer.getPlugin(AssetManagerPlugin)!;
    const assetImporter = this.viewer.getManager()!.importer as AssetImporter;
    const camViewPlugin = this.viewer.getPlugin(CameraViewPlugin)!;
    const groundPlugin = this.viewer.getPlugin(GroundPlugin)!;

    // --- Add scene config ---
    await assetImporter.importPath(`/assets/config/config_${camId}.vjson`, {
      processImported: true,
    });

    // TODO - REVIEW
    this.viewer.enabled = false;
    const temporalPlugin = this.viewer.getPlugin(TemporalAAPlugin);
    temporalPlugin?.fromJSON("/assets/config/preset.TAA.json");

    this.viewer.renderer.refreshPipeline();

    // --- Import modal and apply material from query paramater ---
    const options: ImportAddOptions = {
      autoScale: false,
      autoScaleRadius: 1,
      autoCenter: false,
    };

    if (fileUrl) {
      await manager.addFromPath(fileUrl, options);
    } else {
      await manager.addFromPath("/assets/models/FE0001.glb", options);
      setTimeout(function () {
        window.stageLoaded = true;
        console.log('stageLoaded: ');
      }, 1000);
    }

    // --- Get and store original camera position ---
    this.originalView = camViewPlugin.getCurrentCameraView(
      this.viewer.scene.activeCamera
    );

    // --- For file conversion ---

    // To download the glb file for the given 3d file
    if (downloadFile === "true") {
      // To export GLB
      const exportOptions = {
        compress: false, // This will work if GLTFDracoExportPlugin is added
        name: "scene",
        viewerConfig: false, // This also added the viewer config and plugin settings to the glb
        // convertMeshToIndexed: true,
      };
      // Get the blob
      // @ts-ignore
      const blob = await this.viewer
        .getPlugin(AssetExporterPlugin)
        .exportScene(exportOptions);
      // Download the blob
      // @ts-ignore
      if (blob) downloadBlob(blob, exportOptions.name + "." + blob.ext);
      // setTimeout(async function run() {
      // }, 5000);
    }

    // To download the glb file for the given 3d file
    if (preDownload === "true") {
      // To export GLB
      const exportOptions = {
        compress: false, // This will work if GLTFDracoExportPlugin is added
        name: "scene",
        viewerConfig: false, // This also added the viewer config and plugin settings to the glb
      };
      // Get the blob
      // @ts-ignore
      const blob = await this.viewer
        .getPlugin(AssetExporterPlugin)
        .exportScene(exportOptions);
      // Download the blob
      // @ts-ignore
      if (blob) downloadBlob(blob, exportOptions.name + "." + blob.ext);
      // setTimeout(async function run() {
      // }, 5000);
    }

    // --- Set camera angle and model position ---
    await initModalParams(this.viewer, camId, this.originalView);
    initCameraParams(this.viewer, camId, camAngle);
    await initModelMaterials(this.viewer, this.sceneMetalColor);
    console.log('Welcome to Dream Diamonds');

    // --- Hide loader and show info ---
    this.viewer.enabled = true;
    showLogoLoader(false);

    setTimeout(function () {
      window.stageLoaded = true;
      window.parent?.postMessage({ stageLoaded: true }, '*');
      console.log('stageLoaded: ');
    }, 1000);

    initInfo();

    // To download the glb file for the given 3d file
    // if (downloadFile === "true") {
    //   // To export GLB
    //   const exportOptions = {
    //     compress: false, // This will work if GLTFDracoExportPlugin is added
    //     name: "scene",
    //     viewerConfig: false, // This also added the viewer config and plugin settings to the glb
    //   };
    //   // Get the blob
    //   // @ts-ignore
    //   const blob = await viewer
    //     .getPlugin(AssetExporterPlugin)
    //     .exportScene(exportOptions);
    //   // Download the blob
    //   // @ts-ignore
    //   if (blob) downloadBlob(blob, exportOptions.name + "." + blob.ext);
    //   // setTimeout(async function run() {
    //   // }, 5000);
    // }
    // To be executed by server to capture images and return data
    if (canvasMode === "capture") {
      // @ts-ignore
      document.querySelector(".ole-loader").style.opacity = 0;
      // @ts-ignore
      document.querySelector(".canvas-info").style.opacity = 0;

      if (camId !== "7" && camId !== "5") {
        const newGround = this.viewer.getPlugin(ContactShadowGroundPlugin)!;
        newGround.shadowScale = 0.74;
        newGround.blurAmount = 2.44;
        newGround.shadowHeight = 1;
      }
      // const imageSnapper = await this.viewer.addPlugin(CanvasSnipperPlugin);
      // imageSnapper?.getDataUrl().then((data) => {
      //   const link = document.createElement("a");
      //   link.setAttribute("id", "imageLink");
      //   link.href = data;
      //   document.body.appendChild(link);
      // });
    }

  }

  async downloadGlb() {
    await this.viewer.addPlugin(GLTFDracoExportPlugin);
    const exportOptions = {
      compress: true, // This will work if GLTFDracoExportPlugin is added
      name: "scene",
      viewerConfig: false, // This also added the viewer config and plugin settings to the glb
    };
    // Get the blob
    // console.log('this.viewer: ', this.viewer);
    let exportPlugin = await this.viewer.getPlugin(AssetExporterPlugin);
    const blob = await exportPlugin?.exportScene(exportOptions);
    // Download the blob
    if (blob) downloadBlob(blob, this.productName + "." + blob.ext);
  }

  async changeCameraView(camId = this.camId, angle = 'default') {
    await initModalParams(this.viewer, camId, this.originalView);
    initCameraParams(this.viewer, camId, angle as CameraAngleType);
    this.camId = camId;
    this.viewer.scene.setDirty({ sceneUpdate: true, frameFade: false })
  }

  captureScreenshot() {
    // console.log('captureScreenshot: ');
    let canvas = document.getElementById("ole-canvas") as HTMLCanvasElement;
    // Get the image data as a data URL (PNG format)
    const dataURL = canvas.toDataURL("image/png", 1.0);

    // Extract the base64-encoded image data
    const base64Data = dataURL.split(',')[1];

    // Decode base64 to binary data
    const binaryData = atob(base64Data);

    // Convert binary data to Uint8Array
    const uint8Array = new Uint8Array(binaryData.length);

    for (let i = 0; i < binaryData.length; i++) {
      uint8Array[i] = binaryData.charCodeAt(i);
    }

    // Convert Uint8Array to a regular JavaScript array
    const byteArray = Array.from(uint8Array);

    return byteArray;
  }

  async onMessage(event: any) {
    const { angle, metalColor, loadHand, handColor, importUrl } = event.data;
    const manager = this.viewer.getPlugin(AssetManagerPlugin)!;
    const camViewPlugin = this.viewer.getPlugin(CameraViewPlugin)!;
    const groundPlugin = this.viewer.getPlugin(GroundPlugin)!;

    const options: ImportAddOptions = {
      autoScale: false,
      autoScaleRadius: 1,
      autoCenter: false,
    };

    if (metalColor) {
      await changeMetalColor(this.viewer, metalColor);
      this.sceneMetalColor = metalColor;
    }
    if (handColor) {
      await changeHandColor(this.viewer, handColor, this.camId);
      this._handColor = handColor;
      // this.sceneMetalColor = metalColor;
    }
    if (importUrl) {
      showLogoLoader(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.viewer.scene.disposeSceneModels();
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.viewer.scene.disposeSceneModels();
      await manager.addFromPath(importUrl, options);

      await initModalParams(this.viewer, this.camId, this.originalView);
      await initModelMaterials(this.viewer, this.sceneMetalColor);

      await new Promise((resolve) => setTimeout(resolve, 500));
      if (this._angle === "hand") {
        await loadHandModel(this.viewer, this._handSize);
        await changeHandColor(this.viewer, this._handColor, this.camId);
      }

      showLogoLoader(false);
    }
    if (loadHand && (loadHand === "short" || loadHand === "long")) {
      showLogoLoader(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
      // groundPlugin.enabled = false;
      await loadHandModel(this.viewer, loadHand);
      await changeHandColor(this.viewer, this._handColor, this.camId);
      this._handSize = loadHand;
      showLogoLoader(false);
    }
    if (angle) {
      this._angle = angle || this._angle;
      const angleData = getAngleData(this.camId);
      console.log(
        "angles",
        camViewPlugin.getCurrentCameraView(this.viewer.scene.activeCamera)
      );

      // Get hand mesh and hide
      if (angle !== "hand" && this._showHand === true) {
        this.viewer.scene.modelRoot.traverse(async (m) => {
          if (m.name === "hand") {
            // Hide hand from scene
            m.visible = false;
            groundPlugin.enabled = true;
            this._showHand = false;
            groundPlugin.refreshTransform();
          }
        });
      }
      let _camPosition = { x: 0, y: 0, z: 5 };
      let _camTarget = { x: 0, y: 0, z: 0 };
      let _autoRotate = false;
      let _autoRotateSpeed = 1;
      let _minDistance = 0;
      let _maxDistance = 1000;
      let _minPolarAngle = 0;
      let _maxPolarAngle = 3.14;
      if (angle === "default") {
        const {
          defaultView,
          minDistance,
          maxDistance,
          minPolarAngle,
          maxPolarAngle,
        } = angleData;
        _camPosition = defaultView.camPosition;
        _minDistance = 0;
        _maxDistance = maxDistance;
        if (minPolarAngle) {
          _minPolarAngle = minPolarAngle;
        }
        if (maxPolarAngle) {
          _maxPolarAngle = maxPolarAngle;
        }
      }
      if (angle === "front") {
        // @ts-ignore
        const {
          camViewOne,
          minDistance,
          maxDistance,
          minPolarAngle,
          maxPolarAngle,
        } = angleData;
        _camPosition = camViewOne.camPosition;
        // @ts-ignore
        _camTarget = camViewOne?.camTarget || _camTarget;
        _minDistance = minDistance;
        _maxDistance = maxDistance;

        if (this.camId === "5") {
          _minDistance = 2;
        }
        if (minPolarAngle) {
          _minPolarAngle = minPolarAngle;
        }
        if (maxPolarAngle) {
          _maxPolarAngle = maxPolarAngle;
        }
      }
      if (angle === "side") {
        const {
          camViewTwo,
          minDistance,
          maxDistance,
          minPolarAngle,
          maxPolarAngle,
        } = angleData;
        _camPosition = camViewTwo.camPosition;
        _minDistance = minDistance;
        _maxDistance = maxDistance;
        if (minPolarAngle) {
          _minPolarAngle = minPolarAngle;
        }
        if (maxPolarAngle) {
          _maxPolarAngle = maxPolarAngle;
        }
      }
      if (angle === "hand") {
        if (loadHand === "short") {
          _camPosition = { x: -2.9, y: 3.66, z: 34 };
          _camTarget = { x: 1.31, y: 0.03, z: 0.06 };
          _maxPolarAngle = 2.5;
          _minDistance = 20;
          _maxDistance = 45;
        } else if (loadHand === "long") {
          _camPosition = { x: -2.46, y: 5.8, z: 45 };
          _camTarget = { x: 0.19, y: 3.6, z: -0.94 };
          _minPolarAngle = 1.5;
          _maxPolarAngle = 1.5;
          _minDistance = 30;
          _maxDistance = 60;
        }
        this._showHand = true;
      }
      if (angle === "auto") {
        const { defaultView, minPolarAngle, maxPolarAngle } = angleData;
        _camPosition = defaultView.camPosition;
        _autoRotate = true;
        _autoRotateSpeed = 4;
        if (minPolarAngle) {
          _minPolarAngle = minPolarAngle;
        }
        if (maxPolarAngle) {
          _maxPolarAngle = maxPolarAngle;
        }
      }

      // Get offset data and position data
      let { position, offsetNumber } = getBoundView(
        this.viewer,
        _camPosition,
        this.camId
      );

      if (angle === "hand") {
        offsetNumber = 0;
        position.x = _camPosition.x;
        position.y = _camPosition.y;
        position.z = _camPosition.z;
      }

      // Animate camera to new angle
      let view = camViewPlugin.getCurrentCameraView(this.viewer.scene.activeCamera);
      view.target.set(_camTarget.x, _camTarget.y, _camTarget.z);
      view.position.set(position.x, position.y, position.z);

      // Update camera params
      // @ts-ignore
      this.viewer.scene.activeCamera.controls?.autoRotate = _autoRotate;
      // @ts-ignore
      this.viewer.scene.activeCamera.controls?.autoRotateSpeed = _autoRotateSpeed;
      // @ts-ignore
      this.viewer.scene.activeCamera.controls?.minPolarAngle = _minPolarAngle;
      // @ts-ignore
      this.viewer.scene.activeCamera.controls?.maxPolarAngle = _maxPolarAngle;
      // @ts-ignore
      this.viewer.scene.activeCamera.controls?.minDistance = _minDistance;
      // @ts-ignore
      this.viewer.scene.activeCamera.controls?.maxDistance =
        _maxDistance + offsetNumber;

      if (this._showHand) {
        showLogoLoader(true);
        await new Promise((resolve) => setTimeout(resolve, 500));
        camViewPlugin.animateToView(view, 0);
        showLogoLoader(false);
      } else {
        camViewPlugin.animateToView(view, 1000);
      }
    }
  }

  // Rest of the class methods...
  // (Remaining functions from the provided code as private class methods)
}

new ViewerSetup();
